import React, { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  bootstrapCampaignAccess,
  CampaignAccessApiError,
  mutateCampaignQuestionnaireDraft,
  probeCampaignAccessSession,
  requestCampaignAccessChallenge,
  resumeCampaignAccessSession,
  submitCampaignQuestionnaireResponseRehearsal,
  verifyCampaignAccess,
} from "./campaignAccessApi";
import CampaignReadOnlyQuestionnairePreview from "./CampaignReadOnlyQuestionnairePreview";
import CampaignLocalTextInteractionRehearsal from "./CampaignLocalTextInteractionRehearsal";
import CampaignPersistedQuestionnaireDraft from "./CampaignPersistedQuestionnaireDraft";
import CampaignPersistedMultiAnswerDraft from "./CampaignPersistedMultiAnswerDraft";
import CampaignQuestionnaireResponseReceipt from "./CampaignQuestionnaireResponseReceipt";

const INVALID_LINK_MESSAGE =
  "This Campaign access link is invalid or unavailable.";
const UNAVAILABLE_MESSAGE =
  "Campaign secure access is temporarily unavailable.";
const PAGE_LEFT_MESSAGE =
  "This secure access view was cleared when this page was left.";

const initialState = {
  phase: "loading",
  error: "",
  access: null,
  challenge: null,
  sessionExpiresAt: null,
  questionnairePreview: null,
  draftCurrent: null,
  responseReceipt: null,
};

function consumeFragmentToken() {
  const originalSearch = String(window.location.search || "");
  const fragment = String(window.location.hash || "").replace(/^#/, "");
  const fields = Array.from(new URLSearchParams(fragment).entries());
  const token = fields.length === 1 && fields[0][0] === "token"
    ? fields[0][1]
    : "";

  // Scrub query, fragment, and existing history state before any request.
  try {
    window.history.replaceState(null, document.title, window.location.pathname);
  } catch (_error) {
    return { token: "", resumeEligible: false };
  }
  return {
    token,
    resumeEligible: !token && originalSearch === "" && fragment === "",
  };
}

function formatExpiry(value) {
  const date = new Date(String(value || ""));
  return Number.isFinite(date.getTime()) ? date.toLocaleString() : "Unavailable";
}

function safePublicMessage(error) {
  return error instanceof CampaignAccessApiError && error.publicMessage
    ? error.publicMessage
    : UNAVAILABLE_MESSAGE;
}

function purgeTypedDraft(current) {
  if (!current?.answers) return;
  current.answers.forEach((answer) => {
    if (typeof answer?.value === "string") answer.value = "";
  });
  current.answers.length = 0;
}

function purgeTypedAnswers(answers) {
  if (!answers) return;
  answers.forEach((answer) => {
    if (typeof answer?.value === "string") answer.value = "";
  });
  answers.length = 0;
}

export default function CampaignAccessLandingPage() {
  const [state, setState] = useState(initialState);
  const [otp, setOtp] = useState("");
  const tokenRef = useRef(null);
  const otpInputRef = useRef(null);
  const initialLocationRef = useRef(null);
  const verifiedSessionRef = useRef(null);
  const draftAccessRef = useRef(null);
  const multiAnswerCurrentRef = useRef(null);
  const persistedDraftActiveRef = useRef(false);
  const sessionExpiryTimerRef = useRef(null);
  const sessionProbeRetryUsedRef = useRef(false);
  const bootstrapRequestRef = useRef(null);
  const resumeRequestRef = useRef(null);
  const pageHiddenRef = useRef(false);
  const persistedPageHiddenRef = useRef(false);
  const resumeActionRef = useRef(null);
  const actionControllerRef = useRef(null);
  const lifecycleRef = useRef({ mounted: false, generation: 0 });
  const cleanupGenerationRef = useRef(0);

  const clearVerifiedSessionSecret = useCallback(() => {
    if (sessionExpiryTimerRef.current !== null) {
      clearTimeout(sessionExpiryTimerRef.current);
      sessionExpiryTimerRef.current = null;
    }
    if (verifiedSessionRef.current) verifiedSessionRef.current.csrfToken = "";
    if (draftAccessRef.current) draftAccessRef.current.requestToken = "";
    purgeTypedDraft(multiAnswerCurrentRef.current);
    verifiedSessionRef.current = null;
    draftAccessRef.current = null;
    multiAnswerCurrentRef.current = null;
    persistedDraftActiveRef.current = false;
    sessionProbeRetryUsedRef.current = false;
  }, []);

  const clearPendingPublicRequests = useCallback(() => {
    bootstrapRequestRef.current?.controller.abort();
    bootstrapRequestRef.current = null;
    resumeRequestRef.current?.action.controller.abort();
    resumeRequestRef.current = null;
    resumeActionRef.current = null;
  }, []);

  const expireVerifiedSessionView = useCallback(() => {
    clearVerifiedSessionSecret();
    lifecycleRef.current.generation += 1;
    actionControllerRef.current?.abort();
    setState((current) => ({
      ...current,
      phase: "error",
      error: "The verified rehearsal session has expired.",
      challenge: null,
      sessionExpiresAt: null,
      questionnairePreview: null,
      draftCurrent: null,
      responseReceipt: null,
    }));
  }, [clearVerifiedSessionSecret]);

  const beginAction = useCallback(() => {
    actionControllerRef.current?.abort();
    const controller = new AbortController();
    actionControllerRef.current = controller;
    const generation = lifecycleRef.current.generation + 1;
    lifecycleRef.current.generation = generation;
    return { controller, generation };
  }, []);

  const actionIsCurrent = useCallback((action) => (
    lifecycleRef.current.mounted
    && !pageHiddenRef.current
    && lifecycleRef.current.generation === action.generation
    && actionControllerRef.current === action.controller
  ), []);

  const acceptVerifiedSession = useCallback((session, expectedExpiresAt = null) => {
    const expiryTime = new Date(String(session?.expiresAt || "")).getTime();
    const remainingMs = expiryTime - Date.now();
    if (
      !Number.isFinite(expiryTime)
      || remainingMs <= 0
      || (expectedExpiresAt !== null && session.expiresAt !== expectedExpiresAt)
    ) return false;

    clearVerifiedSessionSecret();
    const persistedV3 = session.questionnairePreview?.schemaVersion
      === "campaign_uat_public_questionnaire_preview.v3";
    const responseV4 = session.questionnairePreview?.schemaVersion
      === "campaign_uat_public_questionnaire_preview.v4";
    const responseV5 = session.questionnairePreview?.schemaVersion
      === "campaign_uat_public_questionnaire_preview.v5";
    const responseDraft = (responseV4 || responseV5)
      && session.responseAccess.state === "draft";
    if (persistedV3) {
      draftAccessRef.current = {
        requestToken: session.draftAccess.requestToken,
        expiresAt: session.draftAccess.expiresAt,
      };
    } else if (responseDraft) {
      draftAccessRef.current = {
        requestToken: session.responseAccess.requestToken,
        expiresAt: session.responseAccess.expiresAt,
      };
    }
    if (responseV5 && responseDraft) {
      multiAnswerCurrentRef.current = {
        ...session.responseAccess.currentDraft,
        answers: session.responseAccess.currentDraft.answers.map((answer) => ({
          ...answer,
        })),
      };
      purgeTypedDraft(session.responseAccess.currentDraft);
    }
    persistedDraftActiveRef.current = persistedV3 || responseV4 || responseV5;
    sessionExpiryTimerRef.current = setTimeout(() => {
      expireVerifiedSessionView();
    }, Math.min(remainingMs, 2_147_483_647));
    setState((current) => ({
      ...current,
      phase: "verified",
      error: "",
      challenge: null,
      sessionExpiresAt: session.expiresAt,
      questionnairePreview: session.questionnairePreview,
      draftCurrent: persistedV3
        ? session.draftAccess.current
        : responseDraft
          ? responseV5
            ? { ...session.responseAccess.currentDraft, answers: [] }
            : session.responseAccess.currentDraft
          : null,
      responseReceipt: (responseV4 || responseV5) && !responseDraft
        ? session.responseAccess.receipt
        : null,
    }));
    return true;
  }, [clearVerifiedSessionSecret, expireVerifiedSessionView]);

  useEffect(() => {
    let active = true;
    const lifecycle = lifecycleRef.current;
    lifecycle.mounted = true;
    cleanupGenerationRef.current += 1;

    if (initialLocationRef.current === null) {
      const consumedLocation = consumeFragmentToken();
      tokenRef.current = consumedLocation.token;
      consumedLocation.token = "";
      initialLocationRef.current = {
        resumeEligible: consumedLocation.resumeEligible,
      };
    }
    const token = tokenRef.current;
    if (!token) {
      if (initialLocationRef.current.resumeEligible) {
        if (!resumeRequestRef.current) {
          const action = beginAction();
          resumeActionRef.current = action;
          resumeRequestRef.current = {
            action,
            promise: resumeCampaignAccessSession({
              signal: action.controller.signal,
            }),
          };
        }
        const pendingResume = resumeRequestRef.current;
        pendingResume.promise.then((session) => {
          if (resumeRequestRef.current === pendingResume) {
            resumeRequestRef.current = null;
          }
          if (resumeActionRef.current === pendingResume.action) {
            resumeActionRef.current = null;
          }
          if (
            !active
            || !actionIsCurrent(pendingResume.action)
            || pageHiddenRef.current
          ) return;
          if (!acceptVerifiedSession(session)) {
            clearVerifiedSessionSecret();
            setState({ ...initialState, phase: "error", error: UNAVAILABLE_MESSAGE });
          }
        }).catch((error) => {
          if (resumeRequestRef.current === pendingResume) {
            resumeRequestRef.current = null;
          }
          if (resumeActionRef.current === pendingResume.action) {
            resumeActionRef.current = null;
          }
          if (!active || !actionIsCurrent(pendingResume.action)) return;
          clearVerifiedSessionSecret();
          setState({
            ...initialState,
            phase: "error",
            error: safePublicMessage(error),
          });
        });
      } else {
        setState({ ...initialState, phase: "error", error: INVALID_LINK_MESSAGE });
      }
      return () => {
        active = false;
        lifecycle.mounted = false;
        const cleanupGeneration = ++cleanupGenerationRef.current;
        Promise.resolve().then(() => {
          if (!lifecycle.mounted
            && cleanupGenerationRef.current === cleanupGeneration) {
            lifecycle.generation += 1;
            clearPendingPublicRequests();
            clearVerifiedSessionSecret();
            actionControllerRef.current?.abort();
            actionControllerRef.current = null;
          }
        });
      };
    }

    // StrictMode replays this effect. One in-memory request is shared across
    // the replay; the fragment is never restored and no duplicate request runs.
    if (!bootstrapRequestRef.current) {
      const controller = new AbortController();
      bootstrapRequestRef.current = {
        controller,
        promise: bootstrapCampaignAccess(token, { signal: controller.signal }),
      };
    }
    const pendingBootstrap = bootstrapRequestRef.current;
    pendingBootstrap.promise.then((access) => {
      if (bootstrapRequestRef.current === pendingBootstrap) {
        bootstrapRequestRef.current = null;
      }
      if (access.uatOtpRehearsalAvailable !== true) tokenRef.current = "";
      if (!active || pageHiddenRef.current) return;
      setState({
        phase: "ready",
        error: "",
        access,
        challenge: null,
        sessionExpiresAt: null,
        questionnairePreview: null,
        draftCurrent: null,
        responseReceipt: null,
      });
    }).catch((error) => {
      if (bootstrapRequestRef.current === pendingBootstrap) {
        bootstrapRequestRef.current = null;
      }
      tokenRef.current = "";
      if (!active || pageHiddenRef.current) return;
      setState({
        ...initialState,
        phase: "error",
        error: safePublicMessage(error),
      });
    });

    return () => {
      active = false;
      lifecycle.mounted = false;
      lifecycle.generation += 1;
      const cleanupGeneration = ++cleanupGenerationRef.current;
      // Distinguish a real unmount from StrictMode's immediate effect replay.
      Promise.resolve().then(() => {
        if (!lifecycle.mounted
          && cleanupGenerationRef.current === cleanupGeneration) {
          tokenRef.current = "";
          clearPendingPublicRequests();
          clearVerifiedSessionSecret();
          actionControllerRef.current?.abort();
          actionControllerRef.current = null;
        }
      });
    };
  }, [
    acceptVerifiedSession,
    actionIsCurrent,
    beginAction,
    clearPendingPublicRequests,
    clearVerifiedSessionSecret,
  ]);

  useEffect(() => {
    const expireIfNeeded = () => {
      if (state.phase !== "verified" || !state.sessionExpiresAt) return;
      const expiresAt = new Date(state.sessionExpiresAt).getTime();
      if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
        expireVerifiedSessionView();
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") expireIfNeeded();
    };
    window.addEventListener("pageshow", expireIfNeeded);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("pageshow", expireIfNeeded);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [expireVerifiedSessionView, state.phase, state.sessionExpiresAt]);

  useEffect(() => {
    const clearPublicSecretsOnPageHide = () => {
      const resumeAllowed = Boolean(
        persistedDraftActiveRef.current
        || verifiedSessionRef.current
        || draftAccessRef.current
        || resumeActionRef.current
        || resumeRequestRef.current
      );
      pageHiddenRef.current = true;
      persistedPageHiddenRef.current = resumeAllowed;
      tokenRef.current = "";
      if (initialLocationRef.current) {
        initialLocationRef.current = { resumeEligible: false };
      }
      if (otpInputRef.current) {
        otpInputRef.current.value = "";
        otpInputRef.current.defaultValue = "";
        otpInputRef.current.removeAttribute("value");
      }
      clearPendingPublicRequests();
      clearVerifiedSessionSecret();
      lifecycleRef.current.generation += 1;
      actionControllerRef.current?.abort();
      actionControllerRef.current = null;
      flushSync(() => {
        setOtp("");
        setState({
          ...initialState,
          phase: resumeAllowed ? "resume_required" : "error",
          error: resumeAllowed
            ? "The response rehearsal view was cleared when this page was left. Reload the active session to continue."
            : PAGE_LEFT_MESSAGE,
        });
      });
    };
    window.addEventListener("pagehide", clearPublicSecretsOnPageHide);
    return () => window.removeEventListener("pagehide", clearPublicSecretsOnPageHide);
  }, [clearPendingPublicRequests, clearVerifiedSessionSecret]);

  const requestCode = async () => {
    if (state.phase !== "ready" || !tokenRef.current) return;
    const action = beginAction();
    setState((current) => ({ ...current, phase: "requesting", error: "" }));
    try {
      const challenge = await requestCampaignAccessChallenge(tokenRef.current, {
        signal: action.controller.signal,
      });
      // The challenge UUID, not the link token, is now the continuation handle.
      tokenRef.current = "";
      if (!actionIsCurrent(action)) return;
      setState((current) => ({
        ...current,
        phase: "challenge",
        error: "",
        challenge,
      }));
    } catch (error) {
      if (!actionIsCurrent(action)) return;
      const terminal = error instanceof CampaignAccessApiError && error.terminal;
      if (terminal) tokenRef.current = "";
      setState((current) => ({
        ...current,
        phase: terminal ? "error" : "ready",
        error: safePublicMessage(error),
        challenge: null,
      }));
    }
  };

  const probeVerifiedSession = async (action) => {
    const pending = verifiedSessionRef.current;
    if (!pending) return;
    try {
      const session = await probeCampaignAccessSession({
        csrfToken: pending.csrfToken,
        signal: action.controller.signal,
      });
      if (!actionIsCurrent(action)) return;
      if (!acceptVerifiedSession(session, pending.expiresAt)) {
        clearVerifiedSessionSecret();
        setState((current) => ({
          ...current,
          phase: "error",
          error: UNAVAILABLE_MESSAGE,
          challenge: null,
          sessionExpiresAt: null,
          questionnairePreview: null,
          draftCurrent: null,
          responseReceipt: null,
        }));
        return;
      }
    } catch (error) {
      if (!actionIsCurrent(action)) return;
      const retryableTransportFailure =
        error instanceof CampaignAccessApiError
        && error.transportFailure
        && !sessionProbeRetryUsedRef.current;
      if (!retryableTransportFailure) clearVerifiedSessionSecret();
      setState((current) => ({
        ...current,
        phase: retryableTransportFailure ? "session_error" : "error",
        error: retryableTransportFailure
          ? "The verified rehearsal session could not be confirmed. Try the session check once more."
          : safePublicMessage(error),
        challenge: null,
        sessionExpiresAt: null,
        questionnairePreview: null,
        draftCurrent: null,
        responseReceipt: null,
      }));
    }
  };

  const verifyCode = async () => {
    if (state.phase !== "challenge"
      || !state.challenge
      || !/^\d{6}$/.test(otp)) return;
    const action = beginAction();
    const submittedOtp = otp;
    clearVerifiedSessionSecret();
    setOtp("");
    setState((current) => ({
      ...current,
      phase: "verifying",
      error: "",
      sessionExpiresAt: null,
      questionnairePreview: null,
      draftCurrent: null,
      responseReceipt: null,
    }));
    try {
      const verified = await verifyCampaignAccess({
        challengeId: state.challenge.challengeId,
        otp: submittedOtp,
        signal: action.controller.signal,
      });
      if (!actionIsCurrent(action)) return;
      const expiryTime = new Date(verified.expiresAt).getTime();
      const remainingMs = expiryTime - Date.now();
      if (!Number.isFinite(expiryTime) || remainingMs <= 0) {
        setState((current) => ({
          ...current,
          phase: "error",
          error: UNAVAILABLE_MESSAGE,
          challenge: null,
          sessionExpiresAt: null,
          questionnairePreview: null,
          draftCurrent: null,
          responseReceipt: null,
        }));
        return;
      }
      verifiedSessionRef.current = {
        csrfToken: verified.csrfToken,
        expiresAt: verified.expiresAt,
      };
      setState((current) => ({
        ...current,
        phase: "checking_session",
        error: "",
        challenge: null,
        sessionExpiresAt: null,
        questionnairePreview: null,
        draftCurrent: null,
        responseReceipt: null,
      }));
      await probeVerifiedSession(action);
    } catch (error) {
      if (!actionIsCurrent(action)) return;
      const terminal = error instanceof CampaignAccessApiError && error.terminal;
      setState((current) => ({
        ...current,
        phase: terminal ? "error" : "challenge",
        error: safePublicMessage(error),
        challenge: terminal ? null : current.challenge,
      }));
    }
  };

  const retrySessionProbe = async () => {
    if (state.phase !== "session_error"
      || !verifiedSessionRef.current
      || sessionProbeRetryUsedRef.current) return;
    sessionProbeRetryUsedRef.current = true;
    const action = beginAction();
    setState((current) => ({ ...current, phase: "checking_session", error: "" }));
    await probeVerifiedSession(action);
  };

  const mutatePersistedDraft = async (mutation) => {
    const draftAccess = draftAccessRef.current;
    if (
      state.phase !== "verified"
      || !draftAccess
      || draftAccess.expiresAt !== state.sessionExpiresAt
      || new Date(draftAccess.expiresAt).getTime() <= Date.now()
    ) throw new CampaignAccessApiError();
    const action = beginAction();
    try {
      const result = await mutateCampaignQuestionnaireDraft({
        requestToken: draftAccess.requestToken,
        mutation,
        sessionExpiresAt: draftAccess.expiresAt,
        signal: action.controller.signal,
      });
      if (!actionIsCurrent(action)) throw new CampaignAccessApiError();
      return result;
    } catch (error) {
      if (!actionIsCurrent(action)) throw new CampaignAccessApiError();
      throw error;
    }
  };

  const applyPersistedDraftMutation = ({
    result,
    savedValue,
    questionRef,
    savedAnswers,
    answerSetRef,
  }) => {
    const multiAnswer = result.schemaVersion
      === "campaign_uat_questionnaire_draft_mutation_result.v2";
    if (multiAnswer) {
      purgeTypedDraft(multiAnswerCurrentRef.current);
      const nextAnswers = result.state === "saved"
        ? savedAnswers.map((answer) => ({ ...answer }))
        : [];
      multiAnswerCurrentRef.current = {
        schemaVersion: "campaign_uat_questionnaire_draft_state.v2",
        state: result.state,
        revision: result.revision,
        changedAt: result.changedAt,
        answerSetRef: result.answerSetRef,
        answers: nextAnswers,
      };
      purgeTypedAnswers(savedAnswers);
    }
    setState((current) => {
      if (multiAnswer) {
        if (
          current.phase !== "verified"
          || current.sessionExpiresAt !== result.expiresAt
          || current.questionnairePreview?.schemaVersion
            !== "campaign_uat_public_questionnaire_preview.v5"
          || current.questionnairePreview.questionnaire.answerSetRef
            !== answerSetRef
        ) return current;
        return {
          ...current,
          draftCurrent: {
            schemaVersion: "campaign_uat_questionnaire_draft_state.v2",
            state: result.state,
            revision: result.revision,
            changedAt: result.changedAt,
            answerSetRef: result.answerSetRef,
            answers: [],
          },
        };
      }
      if (
        current.phase !== "verified"
        || current.sessionExpiresAt !== result.expiresAt
        || ![
          "campaign_uat_public_questionnaire_preview.v3",
          "campaign_uat_public_questionnaire_preview.v4",
        ].includes(current.questionnairePreview?.schemaVersion)
        || current.questionnairePreview.questionnaire.sections[0]
          .questions[0].questionRef !== questionRef
      ) return current;
      return {
        ...current,
        draftCurrent: {
          schemaVersion: "campaign_uat_questionnaire_draft_state.v1",
          state: result.state,
          revision: result.revision,
          changedAt: result.changedAt,
          answers: result.state === "saved"
            ? [{ questionRef, value: savedValue }]
            : [],
        },
      };
    });
  };

  const submitPersistedResponse = async (mutation) => {
    const draftAccess = draftAccessRef.current;
    if (
      state.phase !== "verified"
      || ![
        "campaign_uat_public_questionnaire_preview.v4",
        "campaign_uat_public_questionnaire_preview.v5",
      ].includes(state.questionnairePreview?.schemaVersion)
      || state.responseReceipt !== null
      || !draftAccess
      || draftAccess.expiresAt !== state.sessionExpiresAt
      || new Date(draftAccess.expiresAt).getTime() <= Date.now()
    ) throw new CampaignAccessApiError();
    const action = beginAction();
    try {
      const result = await submitCampaignQuestionnaireResponseRehearsal({
        requestToken: draftAccess.requestToken,
        mutation,
        sessionExpiresAt: draftAccess.expiresAt,
        signal: action.controller.signal,
      });
      if (!actionIsCurrent(action)) throw new CampaignAccessApiError();
      return result;
    } catch (error) {
      if (!actionIsCurrent(action)) throw new CampaignAccessApiError();
      throw error;
    }
  };

  const applySubmittedResponse = (result) => {
    if (draftAccessRef.current) draftAccessRef.current.requestToken = "";
    draftAccessRef.current = null;
    purgeTypedDraft(multiAnswerCurrentRef.current);
    multiAnswerCurrentRef.current = null;
    const receipt = {
      schemaVersion: result.schemaVersion,
      state: result.state,
      responseRevision: result.responseRevision,
      answerCount: result.answerCount,
      ...(result.schemaVersion === "campaign_uat_questionnaire_response_receipt.v2"
        ? { answerSetRef: result.answerSetRef }
        : {}),
      submittedAt: result.submittedAt,
      reviewedAt: result.reviewedAt,
    };
    flushSync(() => {
      setState((current) => {
        if (
          current.phase !== "verified"
          || current.responseReceipt !== null
          || ![
            "campaign_uat_public_questionnaire_preview.v4",
            "campaign_uat_public_questionnaire_preview.v5",
          ].includes(current.questionnairePreview?.schemaVersion)
        ) return current;
        return {
          ...current,
          draftCurrent: null,
          responseReceipt: receipt,
        };
      });
    });
  };

  const terminatePersistedDraftView = (error) => {
    clearVerifiedSessionSecret();
    lifecycleRef.current.generation += 1;
    actionControllerRef.current?.abort();
    actionControllerRef.current = null;
    setState((current) => ({
      ...current,
      phase: "error",
      error: safePublicMessage(error),
      challenge: null,
      sessionExpiresAt: null,
      questionnairePreview: null,
      draftCurrent: null,
      responseReceipt: null,
    }));
  };

  const reloadPersistedDraft = async () => {
    if (
      resumeActionRef.current
      || (!persistedPageHiddenRef.current && ![
        "verified",
        "resume_required",
      ].includes(state.phase))
    ) return;
    pageHiddenRef.current = false;
    persistedPageHiddenRef.current = false;
    clearVerifiedSessionSecret();
    const action = beginAction();
    resumeActionRef.current = action;
    setState((current) => ({
      ...current,
      phase: "resuming",
      error: "",
      sessionExpiresAt: null,
      questionnairePreview: null,
      draftCurrent: null,
      responseReceipt: null,
    }));
    try {
      const session = await resumeCampaignAccessSession({
        signal: action.controller.signal,
      });
      if (!actionIsCurrent(action) || persistedPageHiddenRef.current) return;
      if (!acceptVerifiedSession(session)) {
        terminatePersistedDraftView(new CampaignAccessApiError());
      }
    } catch (error) {
      if (!actionIsCurrent(action)) return;
      terminatePersistedDraftView(error);
    } finally {
      if (resumeActionRef.current === action) resumeActionRef.current = null;
    }
  };

  useEffect(() => {
    const resumeAfterPageShow = () => {
      if (
        !persistedPageHiddenRef.current
        || resumeActionRef.current
      ) return;
      pageHiddenRef.current = false;
      reloadPersistedDraft();
    };
    window.addEventListener("pageshow", resumeAfterPageShow);
    return () => window.removeEventListener("pageshow", resumeAfterPageShow);
  });

  const busy = [
    "loading",
    "requesting",
    "verifying",
    "checking_session",
    "resuming",
  ].includes(state.phase);

  return (
    <main className="min-h-screen bg-slate-100 px-4 py-12">
      <section className="mx-auto max-w-3xl rounded-xl border border-slate-200 bg-white p-7">
        <div className="mb-6 flex items-center gap-3">
          <div
            aria-hidden="true"
            className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-lg font-semibold text-blue-700"
          >
            Q
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">
              Secure Campaign access
            </p>
            <h1 className="text-2xl font-semibold text-slate-900">
              {state.access?.campaignName
                || state.questionnairePreview?.questionnaire?.name
                || "Client questionnaire"}
            </h1>
          </div>
        </div>

        {busy && (
          <p role="status" className="text-sm text-slate-600">
            {state.phase === "loading"
              ? "Checking your secure link…"
              : state.phase === "requesting"
                ? "Requesting the provider-disabled rehearsal code…"
                : state.phase === "verifying"
                  ? "Verifying the rehearsal code…"
                  : state.phase === "resuming"
                    ? "Reloading the active response rehearsal session…"
                    : "Confirming the verified rehearsal session…"}
          </p>
        )}

        {state.error && (
          <div
            role="alert"
            className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          >
            {state.error}
          </div>
        )}

        {state.access && !busy && state.phase !== "verified" && (
          <div className="space-y-5">
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              <p>Email verification is required at{" "}
                <strong>{state.access.maskedDestination || "your email address"}</strong>.
              </p>
              <p className="mt-2">
                Link expires: <strong>{formatExpiry(state.access.expiresAt)}</strong>
              </p>
            </div>

            {state.access.uatOtpRehearsalAvailable ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                The server attested this provider-disabled Testerson 2 rehearsal.
                No email will be sent. Your advisor can reveal the captured
                synthetic code once in the authenticated rehearsal workspace.
              </div>
            ) : (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                Email verification delivery is not available for this link, so it
                cannot continue to a questionnaire.
              </div>
            )}

            {state.phase === "ready" && state.access.uatOtpRehearsalAvailable === true && (
              <button
                type="button"
                onClick={requestCode}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                Request rehearsal code
              </button>
            )}

            {state.challenge && ["challenge", "verifying"].includes(state.phase) && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <p className="text-sm text-blue-900" role="status">
                  Rehearsal code captured for {state.challenge.maskedDestination}.
                  No email was sent. Code expires {formatExpiry(state.challenge.expiresAt)}.
                </p>
                <label className="mt-4 block text-sm font-medium text-slate-800" htmlFor="campaign-rehearsal-otp">
                  6-digit rehearsal code
                </label>
                <input
                  id="campaign-rehearsal-otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  ref={otpInputRef}
                  value={otp}
                  disabled={state.phase === "verifying"}
                  onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-lg tracking-[0.3em] text-slate-900"
                />
                <button
                  type="button"
                  disabled={!/^\d{6}$/.test(otp) || state.phase === "verifying"}
                  onClick={verifyCode}
                  className="mt-3 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Verify rehearsal code
                </button>
              </div>
            )}
          </div>
        )}

        {state.phase === "session_error" && (
          <button
            type="button"
            onClick={retrySessionProbe}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
          >
            Retry verified session check
          </button>
        )}

        {state.phase === "resume_required" && (
          <button
            type="button"
            onClick={reloadPersistedDraft}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
          >
            Reload active response rehearsal
          </button>
        )}

        {state.phase === "verified" && (
          <>
            <div
              className="rounded-lg border border-emerald-200 bg-emerald-50 p-4"
              role="status"
            >
              <h2 className="text-lg font-semibold text-emerald-900">
                {state.questionnairePreview?.schemaVersion
                  === "campaign_uat_public_questionnaire_preview.v5"
                  ? state.responseReceipt?.state === "reviewed"
                    ? "Verified - multi-answer response reviewed"
                    : state.responseReceipt?.state === "submitted"
                      ? "Verified - multi-answer response submitted"
                      : "Verified - multi-answer rehearsal available"
                  : state.questionnairePreview?.schemaVersion
                    === "campaign_uat_public_questionnaire_preview.v4"
                  ? state.responseReceipt?.state === "reviewed"
                    ? "Verified - synthetic response reviewed"
                    : state.responseReceipt?.state === "submitted"
                      ? "Verified - synthetic response submitted"
                      : "Verified - response rehearsal available"
                  : state.questionnairePreview?.schemaVersion
                    === "campaign_uat_public_questionnaire_preview.v3"
                    ? "Verified - saved-draft rehearsal available"
                    : "Verified - local questionnaire rehearsal available"}
              </h2>
              <p className="mt-2 text-sm text-emerald-900">
                The provider-disabled OTP and cookie session were verified.
                Production questionnaire submission and booking remain unavailable.
              </p>
              <p className="mt-2 text-xs text-emerald-800">
                Session expires {formatExpiry(state.sessionExpiresAt)}.
              </p>
            </div>
            <CampaignReadOnlyQuestionnairePreview
              preview={state.questionnairePreview}
            />
            {state.responseReceipt ? (
              <CampaignQuestionnaireResponseReceipt
                receipt={state.responseReceipt}
                sessionExpiresAt={state.sessionExpiresAt}
              />
            ) : state.questionnairePreview?.schemaVersion
              === "campaign_uat_public_questionnaire_preview.v5" ? (
                <CampaignPersistedMultiAnswerDraft
                  key={`${state.sessionExpiresAt}:${state.draftCurrent?.state}:${state.draftCurrent?.revision}`}
                  preview={state.questionnairePreview}
                  currentRef={multiAnswerCurrentRef}
                  expiresAt={state.sessionExpiresAt}
                  onMutate={mutatePersistedDraft}
                  onApplied={applyPersistedDraftMutation}
                  onSubmit={submitPersistedResponse}
                  onSubmitted={applySubmittedResponse}
                  onReload={reloadPersistedDraft}
                  onTerminal={terminatePersistedDraftView}
                />
              ) : [
              "campaign_uat_public_questionnaire_preview.v3",
              "campaign_uat_public_questionnaire_preview.v4",
            ].includes(state.questionnairePreview?.schemaVersion) ? (
                <CampaignPersistedQuestionnaireDraft
                  key={`${state.sessionExpiresAt}:${state.draftCurrent?.state}:${state.draftCurrent?.revision}`}
                  preview={state.questionnairePreview}
                  current={state.draftCurrent}
                  expiresAt={state.sessionExpiresAt}
                  onMutate={mutatePersistedDraft}
                  onApplied={applyPersistedDraftMutation}
                  onSubmit={submitPersistedResponse}
                  onSubmitted={applySubmittedResponse}
                  onReload={reloadPersistedDraft}
                  onTerminal={terminatePersistedDraftView}
                />
              ) : (
                <CampaignLocalTextInteractionRehearsal
                  key={state.sessionExpiresAt}
                  preview={state.questionnairePreview}
                />
              )}
          </>
        )}
      </section>
    </main>
  );
}
